import React from 'react';
import { useResusContext } from './ResusContext';
import { useTranslation } from 'react-i18next';
import {
  getAdministrationUnit,
  getDoseByWeightWithMaxLimitFormatted,
  getDoseByWeightWithMaxLimit,
  calcAmountToAdminister,
  splitRatio
} from './medicationUtils';
import { getMedicationDetails } from './predicateUtils';
import { isHebrewOrArabicText } from './languageUtils';
import { Drug, PredicateEvalResult } from '../Cpr/types';
import './DrugsAndDrips.scss';

interface DrugProps {
  drug: Drug;
  customComment?: string;
  predicateResult?: PredicateEvalResult | null;
}

const DrugComponent: React.FC<DrugProps> = ({
  drug,
  customComment,
  predicateResult
}) => {
  let { age, weight } = useResusContext();
  const { t } = useTranslation(['protocols']);

  const drugDetails = getMedicationDetails(drug, age || 0, weight || 0, t) as Drug;

  const getCommentStyle = (text: string): React.CSSProperties => {
    return (isHebrewOrArabicText(text) ? { direction: 'rtl', textAlign: 'right' } : {}) as React.CSSProperties;
  };

  // If predicate exists but doesn't pass, show grayed out version with message
  if (predicateResult && !predicateResult.isValid) {
    return (
      <div id={`drug-${drug.id}`}
        style={{
        display: 'flex',
        justifyContent: 'space-between',
        textAlign: 'left',
        color: 'var(--page-font-color)',
        opacity: 0.5
      }}>
        <div>
          {drug.name + (drug.howToGive? ' - ' + drug.howToGive : '')}
          <br />
          <span style={{ color: '#ff0000' }}>{predicateResult.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div id={`drug-${drug.id}`}
      style={{ width: '100%', justifyContent: 'space-between', textAlign: 'left', color: 'var(--page-font-color)' }}>
      <div>
        
          <>
            {!drugDetails.type ? (
              <>
                {drugDetails.name}: <strong>{getDoseByWeightWithMaxLimitFormatted(drugDetails, weight)} {drugDetails.dose_unit},</strong>{' '}
                <strong>{calcAmountToAdminister(drugDetails, weight)} {getAdministrationUnit(drugDetails)}</strong> -{' '}
                <span className={drugDetails.howToGive && drugDetails.howToGive.trim() !== 'IV' ? 'bold-text' : ''}>
                  {drugDetails.howToGive}
                </span>
              </>
            ) : (
              <>
                {drugDetails.name}: <strong>{calcAmountToAdminister(drugDetails, weight)} {getAdministrationUnit(drugDetails)}</strong> -{' '}
                <span className={drugDetails.howToGive && drugDetails.howToGive.trim() !== 'IV' ? 'bold-text' : ''}>
                  {drugDetails.howToGive}
                </span>
              </>
            )}
          </>
        {!drugDetails.constant_dose && (
          <>
            <br />
            Calculated dose: {drugDetails.dose_per_kg} {drugDetails.dose_unit}/kg
          </>
        )}
        {drugDetails.shouldDispalyConcentration && (
          <>
            <br />
            Concentration: <strong>{splitRatio(drugDetails.concentration)[0]} {drugDetails.dose_unit} per {splitRatio(drugDetails.concentration)[1]} ml</strong>
          </>
        )}
        {drugDetails.comment && (
          <div className="drug-comment" style={getCommentStyle(drugDetails.comment)}>
            {drugDetails.comment}
          </div>
        )}
        {customComment && (
          <div className="drug-comment" style={getCommentStyle(t(`protocols:${customComment}`))}>
            {t(`protocols:${customComment}`)}
          </div>
        )}
        {drugDetails.warnText && (
          <>
            <div className="drug-comment" style={{ color: 'red' }}>{drugDetails.warnText}</div>
          </>
        )}
        {drugDetails.maxDose && getDoseByWeightWithMaxLimit(drugDetails, weight) === Number(drugDetails.maxDose) && (
          <>
            <div style={{ color: 'red', fontSize: '0.9rem' }}>
              Used max dose of {drugDetails.maxDose} {drugDetails.dose_unit}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DrugComponent;